if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dotsLink: this.querySelectorAll('[js-media-gallery="link"]')
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.videoBlocks = this.querySelectorAll('[data-product-video]');
        this.visibleVideoBlocks = new Set();
        this.onVisibilityChange = this.syncVideoPlayback.bind(this);
        this.initVideoControls();
        this.initSlideObserver();

        if (this.elements.dotsLink.length > 1) {
          this.attachCustomButtonListeners();
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        if (!this.videoBlocks.length) return;
        this.videoObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.25) {
              this.visibleVideoBlocks.add(entry.target);
            } else {
              this.visibleVideoBlocks.delete(entry.target);
            }
          });
          this.syncVideoPlayback();
        }, { threshold: [0, 0.25] });
        this.videoBlocks.forEach((block) => this.videoObserver.observe(block));
        document.addEventListener('visibilitychange', this.onVisibilityChange);
      }

      disconnectedCallback() {
        this.videoObserver?.disconnect();
        this.visibleVideoBlocks.clear();
        this.videoBlocks.forEach((block) => block.querySelector('video')?.pause());
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
      }

      initSlideObserver() {
        const observer = new IntersectionObserver(this.handleIntersection.bind(this), {
          root: this.elements.viewer,
          threshold: 1
        });

        this.elements.viewer.querySelectorAll('.product__media-item').forEach(observer.observe.bind(observer));
      }

      handleIntersection(entries) {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.setActiveSlide(entry.target);
          }
        });
      }

      setActiveSlide(activeSlide) {
        this.elements.viewer
          .querySelectorAll('.product__media-item')
          .forEach((slide) => slide.classList.remove('is-active'));
        activeSlide.classList.add('is-active');

        const activeButtonIndex = Array.from(this.elements.dotsLink).findIndex(
          (button, index) => this.getMediaIdForButton(index) === activeSlide.dataset.mediaId
        );

        this.elements.dotsLink.forEach((button, index) => {
          button.classList.remove('is-active');
          if (index === activeButtonIndex) {
            button.classList.add('is-active');
          }
        });
        this.syncVideoPlayback();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        this.syncVideoPlayback();

        if (prepend) {
          activeMedia?.classList?.remove('hidden');
          const parent = activeMedia.parentElement;
          const firstElement = parent.firstElementChild;

          if (firstElement !== activeMedia) {
            const nextSibling = activeMedia.nextSibling;
            parent.prepend(activeMedia);

            nextSibling ? parent.insertBefore(firstElement, nextSibling) : parent.appendChild(firstElement);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      initVideoControls() {
        this.videoBlocks.forEach((block) => {
          block.querySelector('[js-thumbnail="play"]').addEventListener('click', () => {
            delete block.dataset.videoPaused;
            this.playVideo(block);
          });
          block.querySelector('[js-thumbnail="pause"]').addEventListener('click', () => {
            block.dataset.videoPaused = 'true';
            block.querySelector('video')?.pause();
          });
        });
      }

      canPlayVideo(block) {
        const usesActiveSlide = this.mql.matches
          ? this.dataset.desktopLayout.includes('thumbnail')
          : Boolean(this.elements.viewer.querySelector('.slider--mobile'));
        return document.visibilityState !== 'hidden' && this.visibleVideoBlocks.has(block) &&
          (!usesActiveSlide || block.closest('.product__media-item')?.classList.contains('is-active'));
      }

      syncVideoPlayback() {
        this.videoBlocks.forEach((block) => {
          if (this.canPlayVideo(block) && block.dataset.videoPaused !== 'true') {
            this.playVideo(block);
          } else {
            block.querySelector('video')?.pause();
          }
        });
      }

      playVideo(block) {
        if (!this.canPlayVideo(block)) return;
        let video = block.querySelector('video');
        if (!video) {
          video = block.querySelector('[data-product-video-template]').content.querySelector('video').cloneNode(true);
          video.muted = true;
          const updateControls = () => {
            block.querySelector('[js-thumbnail="play"]').classList.toggle('hidden', !video.paused);
            block.querySelector('[js-thumbnail="pause"]').classList.toggle('hidden', video.paused);
          };
          video.addEventListener('play', () => {
            if (!this.canPlayVideo(block)) video.pause();
            updateControls();
          });
          video.addEventListener('pause', updateControls);
          video.addEventListener('ended', updateControls);
          block.querySelector('[data-video-poster]').replaceWith(video);
        }
        if (video.paused) {
          video.play().catch(() => {
            block.querySelector('[js-thumbnail="play"]').classList.remove('hidden');
            block.querySelector('[js-thumbnail="pause"]').classList.add('hidden');
          });
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      attachCustomButtonListeners() {
        this.elements.dotsLink.forEach((button, index) => {
          button.addEventListener('click', () => {
            this.setActiveMedia(this.getMediaIdForButton(index), false);
            this.elements.dotsLink.forEach((btn) => btn.classList.remove('is-active'));
            button.classList.add('is-active');
          });
        });
      }

      getMediaIdForButton(index) {
        const visibleSlides = Array.from(this.elements.viewer.querySelectorAll('[id^="Slide-"]')).filter(
          (slide) => !slide.classList.contains('hidden')
        );
        return visibleSlides[index]?.dataset.mediaId || null;
      }
    }
  );
}
document.addEventListener('DOMContentLoaded', () => {
  document
    .querySelectorAll('.product__slider-buttons .slideshow__control-wrapper .slider-counter__link')
    .forEach(
      (dotButtons, index) =>
        index >= document.querySelectorAll('.product__media-list li:not(.hidden)').length && dotButtons.remove()
    );
});

